// "Export" on the tender list. Two tabs: the list as it is shown (filters,
// search and tag dropdowns applied) as a CSV that Excel opens directly, with
// the columns to pick — "full record" columns fetch every row's details
// first, a few at a time, with a progress bar; and the market chart — new
// SIMAP tenders per day with a rolling average — as PNG or CSV.
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, IconButton, LinearProgress, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { EChart as RawEChart } from '@kbox-labs/react-echarts';

import { EXPORT_COLUMNS, DEFAULT_EXPORT_COLUMN_KEYS, tenderCsv, exportFilename, needsDetails, loadInBatches } from './tenderExport';
import type { ExportableTender } from './tenderExport';
import { rollingMean, dailyStatsCsv, statsFilename, ROLLING_WINDOW } from './tenderStats';
import type { DailyTenderStats } from './tenderStats';
import { downloadCsv } from './csv';
import { startOfToday } from './tenderSearch';

// The library's prop typing is stricter than the option objects ECharts
// accepts; the admin page casts the same way.
const EChart = RawEChart as unknown as React.ComponentType<Record<string, unknown>>;

// Remembered in the browser: the columns picked last time.
const EXPORT_COLUMNS_KEY = 'tenderq.exportColumns';

const loadColumnKeys = (): Set<string> => {
    try {
        const stored = localStorage.getItem(EXPORT_COLUMNS_KEY);
        if (stored) {
            const keys = JSON.parse(stored);
            if (Array.isArray(keys)) return new Set(keys.filter((k): k is string => typeof k === 'string'));
        }
    } catch { /* private mode or bad value: fall through to the default */ }
    return new Set(DEFAULT_EXPORT_COLUMN_KEYS);
};

const saveColumnKeys = (keys: Set<string>): void => {
    try { localStorage.setItem(EXPORT_COLUMNS_KEY, JSON.stringify(Array.from(keys))); } catch { /* not remembered */ }
};

const RANGES = [30, 90, 365] as const;
type Range = typeof RANGES[number];

interface Props {
    open: boolean;
    onClose: () => void;
    // The rows to export, in list order.
    tenders: ExportableTender[];
    // A tender's full record (GET /tenders/{id}): summary, requirements,
    // criteria, the score breakdown. Fetched only for "full record" columns.
    loadDetails: (id: string) => Promise<ExportableTender>;
    // New SIMAP tenders per day for the last n days (GET /tenders/stats/daily).
    loadMarket: (days: number) => Promise<DailyTenderStats>;
}

// Parallel detail fetches while exporting.
const DETAIL_CONCURRENCY = 6;

const TenderExportDialog: React.FC<Props> = ({ open, onClose, tenders, loadDetails, loadMarket }) => {
    const [tab, setTab] = useState<'tenders' | 'market'>('tenders');
    const [selected, setSelected] = useState<Set<string>>(loadColumnKeys);
    const [range, setRange] = useState<Range>(90);
    // While the full records are fetched: done / total; then how many failed.
    const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
    const [failed, setFailed] = useState(0);

    const update = (next: Set<string>) => { setSelected(next); saveColumnKeys(next); };
    const toggle = (key: string) => {
        const next = new Set(selected);
        if (next.has(key)) next.delete(key); else next.add(key);
        update(next);
    };

    const handleDownload = async () => {
        const keys = Array.from(selected);
        let rows = tenders;
        setFailed(0);
        if (needsDetails(keys)) {
            setProgress({ done: 0, total: tenders.length });
            const full = await loadInBatches(tenders.map(t => t.id), loadDetails, DETAIL_CONCURRENCY,
                done => setProgress({ done, total: tenders.length }));
            setProgress(null);
            // A row whose record could not be fetched keeps its list fields
            // and gets blank detail cells.
            rows = tenders.map((t, i) => full[i] ? { ...t, ...full[i] } : t);
            const missing = full.filter(f => f === null).length;
            setFailed(missing);
            downloadCsv(exportFilename(new Date()), tenderCsv(rows, keys, startOfToday()));
            if (missing > 0) return;
        } else {
            downloadCsv(exportFilename(new Date()), tenderCsv(rows, keys, startOfToday()));
        }
        onClose();
    };
    const exporting = progress !== null;

    // Fetched only once the Market tab is open; kept for the session.
    const marketQuery = useQuery({
        queryKey: ['tenderStats', range],
        queryFn: () => loadMarket(range),
        enabled: open && tab === 'market',
        staleTime: 10 * 60 * 1000,
        retry: 0,
    });
    const days = marketQuery.data?.days ?? [];
    const counts = days.map(d => d.tenders);
    const mean = rollingMean(counts, ROLLING_WINDOW);
    const total = counts.reduce((a, b) => a + b, 0);

    const rows = tenders.length;
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pr: 6, pb: 0 }}>
                Export
                <IconButton aria-label="close" onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
                <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mt: 1, minHeight: 36 }}>
                    <Tab value="tenders" label="Tender list" sx={{ textTransform: 'none', minHeight: 36, py: 0 }} />
                    <Tab value="market" label="Market: tenders per day" sx={{ textTransform: 'none', minHeight: 36, py: 0 }} />
                </Tabs>
            </DialogTitle>
            {tab === 'tenders' ? (
                <>
                    <DialogContent dividers>
                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                            Exports {rows} {rows === 1 ? 'row' : 'rows'}: the list as it is shown now, with the filters, search and dropdowns applied.
                            One row per lot. CSV, opens in Excel.
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle2">Columns</Typography>
                            <Button size="small" onClick={() => update(new Set(EXPORT_COLUMNS.map(c => c.key)))} sx={{ textTransform: 'none', minWidth: 0 }}>Select all</Button>
                            <Button size="small" onClick={() => update(new Set())} sx={{ textTransform: 'none', minWidth: 0 }}>None</Button>
                            <Button size="small" onClick={() => update(new Set(DEFAULT_EXPORT_COLUMN_KEYS))} sx={{ textTransform: 'none', minWidth: 0 }}>Default</Button>
                        </Box>
                        {(['list', 'details'] as const).map(group => (
                            <Box key={group} sx={{ mb: 1 }}>
                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.25 }}>
                                    {group === 'list'
                                        ? 'From the list'
                                        : 'From each tender\'s full record (summary, requirements, criteria, score breakdown). Fetched per tender when exporting: about 5 seconds per 100 rows.'}
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', columnGap: 1 }}>
                                    {EXPORT_COLUMNS.filter(c => c.group === group).map(column => (
                                        <FormControlLabel key={column.key}
                                            control={<Checkbox size="small" checked={selected.has(column.key)} onChange={() => toggle(column.key)} disabled={exporting} />}
                                            label={column.label}
                                            sx={{ mr: 0, '& .MuiTypography-root': { fontSize: '0.8rem' } }} />
                                    ))}
                                </Box>
                            </Box>
                        ))}
                        {progress && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="caption">Fetching full records… {progress.done} / {progress.total}</Typography>
                                <LinearProgress variant="determinate" value={progress.total ? (100 * progress.done) / progress.total : 0} />
                            </Box>
                        )}
                        {failed > 0 && !exporting && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                The file was downloaded, but {failed} {failed === 1 ? 'record' : 'records'} could not be fetched; those rows have blank detail cells.
                            </Alert>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose} disabled={exporting} sx={{ textTransform: 'none' }}>{failed > 0 ? 'Close' : 'Cancel'}</Button>
                        <Button variant="contained" onClick={handleDownload} disabled={exporting || rows === 0 || selected.size === 0} sx={{ textTransform: 'none' }}>
                            {exporting ? 'Exporting…' : 'Download CSV'}
                        </Button>
                    </DialogActions>
                </>
            ) : (
                <>
                    <DialogContent dividers>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                New tenders published on SIMAP per day, all industries, with a {ROLLING_WINDOW}-day average.
                                {marketQuery.data && ` ${total} in the last ${range} days.`}
                            </Typography>
                            <ToggleButtonGroup size="small" exclusive value={range}
                                onChange={(_e, v: Range | null) => { if (v !== null) setRange(v); }}>
                                {RANGES.map(r => (
                                    <ToggleButton key={r} value={r} sx={{ textTransform: 'none', py: 0.25, px: 1, fontSize: '0.75rem' }}>{r} days</ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Box>
                        {marketQuery.isError && <Alert severity="warning" sx={{ mb: 1 }}>The tender statistics are not available right now.</Alert>}
                        {marketQuery.isPending && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 260 }}><CircularProgress size={24} /></Box>
                        )}
                        {marketQuery.data && (
                            <EChart
                                style={{ width: '100%', height: 260 }}
                                grid={{ left: 40, right: 16, top: 36, bottom: 40 }}
                                toolbox={{ right: 8, top: 0, feature: { saveAsImage: { name: `tenders-per-day-${range}d`, title: 'Save as PNG' } } }}
                                legend={{ top: 0, left: 0, textStyle: { fontSize: 11 } }}
                                tooltip={{ trigger: 'axis' }}
                                xAxis={{ type: 'category', data: days.map(d => d.date), axisLabel: { fontSize: 10, hideOverlap: true } }}
                                yAxis={{ type: 'value', minInterval: 1, axisLabel: { fontSize: 10 } }}
                                series={[
                                    { name: 'New tenders', type: 'bar', data: counts, itemStyle: { color: '#90caf9' } },
                                    { name: `${ROLLING_WINDOW}-day average`, type: 'line', data: mean, smooth: true, showSymbol: false,
                                      lineStyle: { width: 2, color: '#1565c0' } },
                                ]}
                            />
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Typography variant="caption" sx={{ flexGrow: 1, pl: 1, color: 'text.secondary' }}>
                            PNG: the camera icon on the chart.
                        </Typography>
                        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
                        <Button variant="contained" disabled={days.length === 0} sx={{ textTransform: 'none' }}
                            onClick={() => downloadCsv(statsFilename(new Date()), dailyStatsCsv(days))}>
                            Download CSV
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default TenderExportDialog;
